/** Published USD token rates, per million tokens, verified 2026-09-29.
 * OpenAI: https://developers.openai.com/api/docs/pricing
 * Opus: https://ai-gateway.vercel.sh/v1/models
 * Used for picker disclosure and direct Ultrafast usage accounting.
 */
export const MODEL_TOKEN_PRICES = {
  amoreg: {
    input: 2,
    cachedInput: 0.1,
    cacheWrite: 2.5,
    output: 10,
    longContext: { threshold: 272_000, input: 4, cachedInput: 0.2, cacheWrite: 5, output: 15 },
  },
  "amoreg-flash": {
    input: 60,
    cachedInput: 6,
    cacheWrite: 75,
    output: 300,
    longContext: { threshold: 272_000, input: 120, cachedInput: 12, cacheWrite: 150, output: 450 },
  },
  "amoreg-pro": { input: 4, cachedInput: 0.2, cacheWrite: 5, output: 20 },
} as const;

/**
 * Direct OpenAI Ultrafast rates per OpenAI model id: six times the model's
 * standard rate. GPT-6 Astra is the `amoreg-flash` worker tier; GPT-6.1 Sol
 * is the composer's Ultrafast service tier on the standard model.
 */
export const ULTRAFAST_TOKEN_PRICES = {
  "gpt-6-astra": MODEL_TOKEN_PRICES["amoreg-flash"],
  "gpt-6.1-sol": {
    input: 12,
    cachedInput: 0.6,
    cacheWrite: 15,
    output: 60,
    longContext: { threshold: 272_000, input: 24, cachedInput: 1.2, cacheWrite: 30, output: 90 },
  },
} as const;

export type UltrafastModelId = keyof typeof ULTRAFAST_TOKEN_PRICES;

export function isUltrafastModelId(value: unknown): value is UltrafastModelId {
  return typeof value === "string" && Object.hasOwn(ULTRAFAST_TOKEN_PRICES, value);
}

/** Price provider-reported tokens, not estimates based on text length.
 * Reasoning is already included in outputTokens. Cache writes are part of input.
 */
export function priceUltrafastTokens(
  tokens: {
    inputTokens: number;
    outputTokens: number;
    cacheReadTokens: number;
    cacheWriteTokens: number;
  },
  model: UltrafastModelId = "gpt-6-astra",
): number {
  const price = ULTRAFAST_TOKEN_PRICES[model];
  const rate = tokens.inputTokens > price.longContext.threshold ? price.longContext : price;
  const read = Math.min(tokens.cacheReadTokens, tokens.inputTokens);
  const write = Math.min(tokens.cacheWriteTokens, tokens.inputTokens - read);
  return (
    ((tokens.inputTokens - read - write) * rate.input +
      read * rate.cachedInput +
      write * rate.cacheWrite +
      tokens.outputTokens * rate.output) /
    1_000_000
  );
}
